import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const clusteringCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Company',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.companies.`,
});

export async function getCompanies(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = clusteringCache.getFromMemory('all');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('COMPANIES');
			if (legacyData) {
				log('info', 'Company', '📦 Seeding Vue cache from legacy storage (COMPANIES)');
				await clusteringCache.put('all', legacyData);
				clusteringCache.setInMemory('all', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'Company', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	try {
		const cached = await clusteringCache.get('all');
		if (cached && !forceRefresh) {
			clusteringCache.setInMemory('all', cached);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Company', '📴 Offline: Returning cached companies');
				return cached;
			}

			setTimeout(() => refreshCompaniesInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Company', '📴 Offline: No cached companies available');
			return [];
		}

		return await fetchCompaniesFromAPI();
	} catch (error) {
		log('error', 'Company', `❌ Error getting companies: ${error.message}`);

		try {
			const fallback = await clusteringCache.get('all');
			if (fallback) {
				log('info', 'Company', '💾 Using fallback cached companies');
				return fallback;
			}
		} catch (cacheError) {
			log('error', 'Company', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchCompaniesFromAPI() {
	try {
		const response = await authenticatedFetch(getApiUrl('Core/Companies'), { method: 'GET' }, 'tokenSales');

		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const companies = await response.json();
		await clusteringCache.put('all', companies);
		clusteringCache.setInMemory('all', companies);

		log('success', 'Company', `✅ Fetched and cached ${companies.length} companies`);
		return companies;
	} catch (error) {
		log('error', 'Company', `❌ Failed to fetch companies from API: ${error.message}`);
		throw error;
	}
}

async function refreshCompaniesInBackground() {
	try {
		await fetchCompaniesFromAPI();
		log('success', 'Company', '🔄 Background refresh of companies completed');
	} catch (error) {
		log('warn', 'Company', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshCompanies() {
	clusteringCache.clearMemory();
	await clusteringCache.clear();
}
